import React from 'react';
import './InputPhoneNumber.scss';
import {
    SELECT_COUNTRY_IS_OPEN_DEFAULT,
    SEARCH_TERM_DEFAULT,
    FORMATED_NUMBER_DEFAULT,
} from './constants';
import MagnifyingGlassIcon from './MagnifyingGlassIcon';
import { 
    parsePhoneNumberFromString, 
    getCountries, 
    getCountryCallingCode 
} from 'libphonenumber-js';

export const InputPhoneNumber = ({
    countriesLocale,
    language = 'ru',
    onChange,
    value,
    formattedNumber,
    setFormattedNumber,
    resetValidateError,
    touched,
    selectedCountryCode,
    setSelectedCountryCode,
    ...restProps
}) => {
    const countries = getCountries();

    const countryOptions = countries.flatMap(countryCode => {
        const countryLocales =  countriesLocale.find(
            ({ countryISOCode }) => countryISOCode === countryCode
        );

        return countryLocales 
            ? [{
                value: countryCode, 
                label: countryLocales[`label_${language}`], 
                dialCode: `+${getCountryCallingCode(countryCode)}`
            }]
            : [];
    });

    const [isSelectCountryOpen, setIsSelectCountryOpen] = React.useState(SELECT_COUNTRY_IS_OPEN_DEFAULT);
    const [searchTerm, setSearchTerm] = React.useState(SEARCH_TERM_DEFAULT);

    const selectRef = React.useRef(null);
    const inputPhoneNumberRef = React.useRef(null);
    const inputSearchRef = React.useRef(null);

    const toggleSelectListCountries = () => {
        setSearchTerm(SEARCH_TERM_DEFAULT);
        setIsSelectCountryOpen(state => !state);
    };

    const handleClickOutside = (event) => {
        if (selectRef.current && !selectRef.current.contains(event.target)) {
            setIsSelectCountryOpen(false);
        }
    };

    const selectCountryCode = (countryCode) => {
        setFormattedNumber(FORMATED_NUMBER_DEFAULT);
        setSelectedCountryCode(countryCode);
        setIsSelectCountryOpen(SELECT_COUNTRY_IS_OPEN_DEFAULT);
        if (inputPhoneNumberRef.current) {
            inputPhoneNumberRef.current.focus();
        }
        resetValidateError();
    }

    const focusSearchField = () => {
        if(inputSearchRef.current) {
            inputSearchRef.current.focus()
        }
    }

    const findCountry = (event) => {
        const searchValue = event.target.value;
        setSearchTerm(searchValue);
    }

    const filteredCountries = countryOptions.filter(country => {
        const countryName = country.label.toLowerCase();
        return countryName.includes(searchTerm?.toLowerCase());
    });

    const handleChange = React.useCallback((event) => {
        const inputValue = event.target.value;
        const phoneNumberObject = parsePhoneNumberFromString(inputValue);
        if (phoneNumberObject) {
            setFormattedNumber(phoneNumberObject.formatInternational());
            if (phoneNumberObject.country && phoneNumberObject.country !== selectedCountryCode) {
                setSelectedCountryCode(phoneNumberObject.country);
            }
        } else {
            setFormattedNumber(FORMATED_NUMBER_DEFAULT);
        }

        onChange(event);
    }, [ selectedCountryCode, setFormattedNumber, setSelectedCountryCode, onChange ]);
    
    React.useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    React.useEffect(() => {
        const inputPhone = inputPhoneNumberRef.current;
        if (inputPhone) {
            if (!formattedNumber) {
                const countryCallingCode = getCountryCallingCode(selectedCountryCode);
                inputPhone.value = `+${countryCallingCode}`;
                if (touched) {
                    inputPhone.focus();
                    const inputPhoneLength = inputPhone.value.length;
                    inputPhone.setSelectionRange(inputPhoneLength, inputPhoneLength);
                }
            } else {
                inputPhone.value = formattedNumber;
                inputPhone.focus();
                if (touched) {
                    inputPhone.focus();
                }
            }
        }
    }, [formattedNumber, selectedCountryCode, touched]);

    return ( 
        <div className='InputPhoneNumber' ref={selectRef}>
            <div className="InputPhoneNumber-PhoneFieldBox">
                <div 
                    className="InputPhoneNumber-PhoneCountryBox" 
                    role="button" 
                    onClick={toggleSelectListCountries}
                >
                    <div className='InputPhoneNumber-ISOCode'>
                        {selectedCountryCode}
                    </div>
                    <div className="InputPhoneNumber-Arrow" />
                </div>
                <div className="InputPhoneNumber-PhoneInputBox">
                    <input 
                        {...restProps}
                        ref={inputPhoneNumberRef}
                        onChange={handleChange}
                        className="InputPhoneNumber-PhoneInput" 
                        type="text"    
                    />
                </div>
            </div>
            
            {isSelectCountryOpen && <div className="InputPhoneNumber-DropdownBox">
                <div className="InputPhoneNumber-SearchDropdownBox">
                    <div className="InputPhoneNumber-SearchDropdownFieldBox">
                        <div 
                            className="InputPhoneNumber-SearchDropdownBoxImageContainer" 
                            role="button"
                            onClick={focusSearchField}
                        >
                            <MagnifyingGlassIcon />
                        </div>
                        <input
                            ref={inputSearchRef}
                            onInput={findCountry}
                            className="InputPhoneNumber-SearchDropdown" 
                            type="text" 
                        />
                    </div>
                </div>
                <div className="InputPhoneNumber-DropdownListBox">
                    <ul className="InputPhoneNumber-DropdownList">
                        {filteredCountries.map(country => (
                            <li key={country.value} className="InputPhoneNumber-DropdownListItemBox">
                                <div 
                                    className="InputPhoneNumber-DropdownListItemContent" 
                                    role="button"
                                    onClick={() => selectCountryCode(country.value)}
                                >
                                    <div className="InputPhoneNumber-DropdownListItemContentISO">
                                        {country.value}
                                    </div>
                                    <div className="InputPhoneNumber-DropdownListItemContentLabel">
                                        {country.label}
                                    </div>
                                    <div className="InputPhoneNumber-DropdownListItemContentDialCode">
                                        {country.dialCode}
                                    </div>
                                </div> 
                            </li>
                        ))}
                    </ul>
                </div>
            </div>}
        </div>
    );
}
